import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import QuoteForm from "@/components/QuoteForm";
import { useSite } from "@/lib/SiteContext";
import { IMG, isPlaceholder } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

export default function Contact() {
  useSeo("Contact Us | RD Cleaning Services Perth", "Get in touch with RD Cleaning Services for residential and commercial cleaning across Perth, WA. Request a free quote or contact our team today.");
  const { settings } = useSite();
  return (
    <>
      <PageHero
        eyebrow="Contact Us"
        title="Let's Get Your Space Looking Its Best"
        subtitle="Reach out for a free, no-obligation quote — or send us a message and we'll get back to you as soon as possible."
        image={IMG.interior}
      />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <Reveal className="lg:col-span-2">
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">RD Cleaning Services</h2>
            <p className="mt-3 text-slate-600">Professional residential &amp; commercial cleaning across Perth.</p>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Phone className="h-5 w-5" /></div>
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Phone</p>
                  {isPlaceholder(settings.phone) ? (
                    <p className="mt-1 font-semibold text-navy" data-testid="contact-phone">{settings.phone}</p>
                  ) : (
                    <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="focus-ring mt-1 block rounded font-semibold text-navy hover:text-teal-700" data-testid="contact-phone">{settings.phone}</a>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Mail className="h-5 w-5" /></div>
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Email</p>
                  {isPlaceholder(settings.email) ? (
                    <p className="mt-1 font-semibold text-navy" data-testid="contact-email">{settings.email}</p>
                  ) : (
                    <a href={`mailto:${settings.email}`} className="focus-ring mt-1 block rounded font-semibold text-navy hover:text-teal-700" data-testid="contact-email">{settings.email}</a>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><MapPin className="h-5 w-5" /></div>
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Service Area</p>
                  <p className="mt-1 font-semibold text-navy" data-testid="contact-area">{settings.service_area}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Clock className="h-5 w-5" /></div>
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Availability</p>
                  <p className="mt-1 font-semibold text-navy">Flexible scheduling — tell us what suits you</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-3">
            <QuoteForm tid="contact" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
